import { useEffect, useState } from "react";

// vite-plugin-pwa injects virtual modules when registered. We import them lazily
// so the module can be unit-tested without the PWA plugin active.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — virtual module provided by vite-plugin-pwa
import { registerSW } from "virtual:pwa-register";

export interface PwaUpdateState {
  needRefresh: boolean;
  offlineReady: boolean;
  updateServiceWorker(): Promise<void>;
}

/**
 * Subscribe to vite-plugin-pwa's update lifecycle. Returns null when the
 * service worker isn't available (dev without PWA, or unsupported browser).
 */
export function usePwaUpdate(): PwaUpdateState | null {
  const [state, setState] = useState<PwaUpdateState | null>(null);

  useEffect(() => {
    // vite-plugin-pwa's registerSW returns the function used to apply a waiting
    // service-worker update: calling it with `true` tells the waiting SW to
    // skip waiting and reloads the page so the new version takes over. It does
    // NOT unregister the service worker — unregistering would break offline
    // support and is not what we want on unmount. We capture it so the
    // update/reload button below can trigger the waiting update.
    let update: ((reloadPage?: boolean) => Promise<void>) | undefined;

    try {
      update = registerSW({
        immediate: true,
        onNeedRefresh() {
          setState({
            needRefresh: true,
            offlineReady: false,
            updateServiceWorker: () => update!(true),
          });
        },
        onOfflineReady() {
          setState({
            needRefresh: false,
            offlineReady: true,
            updateServiceWorker: () => update!(true),
          });
        },
      });
    } catch {
      // virtual:pwa-register is unavailable (test env). Treat as no PWA.
      setState(null);
    }

    // No cleanup: we intentionally do not call update() here. Calling it would
    // either no-op or force an update/reload at the wrong time; the service
    // worker registration itself is managed by vite-plugin-pwa and should persist
    // so offline support keeps working across remounts/HMR.
  }, []);

  return state;
}

export interface BeforeInstallPromptEvent extends Event {
  platforms: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

/**
 * Track the Chromium `beforeinstallprompt` event. Safari/iOS do not fire it, so
 * `canPrompt` stays false there and UI falls back to manual instructions.
 */
export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handler = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const installedHandler = () => setDeferred(null);

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  return {
    canPrompt: deferred !== null,
    async promptInstall() {
      if (!deferred) {
        return false;
      }

      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      return choice.outcome === "accepted";
    },
  };
}

/** True when the app is running inside an installed standalone display mode. */
export function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (navigator as any).standalone === true
  );
}

/** Track online/offline status reactively. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return online;
}
